import type { Fact } from './facts.js';
import { familyRelationFrom, isFamilyRelation } from './family.js';
import { formatLoose, formatMonth, joinClauses, plural } from './format.js';
import { pairSide, refEntityId, sameEntityId } from './pair-side.js';
import { ORG_RELATIONS, type FactType, type NationalityBasis, type OrgRelation } from './vocab.js';

/**
 * Facts arrive as `{fact_type, fields}` where `fields` is the catalog's jsonb
 * (server/src/common/catalog/fact-types.ts). This is the reader's-direction
 * render: one sentence per fact, the subject implicit, the counterparty named.
 *
 * Everything is defensive — `fields` is an opaque `Record<string, unknown>` on
 * the wire, so a payload that does not match the catalog degrades to a readable
 * line instead of throwing.
 */

/**
 * **What the answer will say, in the office's own words, before it is sent** —
 * and the only place the two picks are put together, so a reader who chose the
 * wrong side sees it as a sentence rather than as a lit button.
 *
 * It is deliberately **the same sentence the server writes** onto the
 * `org_relation` fact's `detail` (`server/src/modules/facts/org-relation-sentence.ts`,
 * `orgRelationSentence`): what a surface promised and what the record ends up
 * saying are one string, so nobody has to hold two wordings in their head. Names
 * only, and never an id — as everywhere else on this card.
 *
 * **Typed over the catalog's four relations**, on both sides now: the merge card
 * can only produce three of them, but the knowledge panel renders whatever the
 * extractor wrote and the link modal offers `hosted` outright, so all four have
 * to be sayable. The two copies are the same words byte for byte, which is what
 * `needs-action.spec.tsx` and the server's own int specs pin.
 *
 * The parameters are the fact's two columns in order: `subject` is `org` — the
 * smaller side, the governed one, or the host — and `counterparty` is the whole,
 * the governing body, or the event. Only `hosted` reads them left to right.
 */
export function orgRelationSentence(
  relation: OrgRelation,
  subject: string,
  counterparty: string,
): string {
  switch (relation) {
    case 'part_of':
      return `${subject} is part of ${counterparty}.`;
    case 'governed_by':
      return `${counterparty} governs ${subject}.`;
    case 'affiliated_with':
      return `${subject} and ${counterparty} are affiliated.`;
    case 'hosted':
      return `${subject} hosted ${counterparty}.`;
    case 'sponsored':
      return `${subject} sponsored ${counterparty}.`;
  }
}

type Fields = Record<string, unknown>;

/**
 * What `factLine` actually reads: a type, its fields, and the window when the
 * projection computed one. Deliberately narrower than `Fact` so the same
 * sentence can be written for a finding that has **no row on this page** — a
 * queued question whose lineage is filed under the counterparty, or one a later
 * run left out — which is what the Flagged findings section renders. A `Fact` is
 * assignable to it, so every existing caller is unchanged.
 */
export interface FactStatementSource {
  fact_type: FactType;
  fields: Fields;
  window?: Fact['window'];

  /**
   * The fact's own two entity columns and their live names. Optional only so a
   * row with no lineage can still be rendered from `fields` alone (Flagged
   * findings); every real `Fact` carries all four.
   */
  subject_entity_id?: string | null;
  counterparty_entity_id?: string | null;
  subject_display_name?: string | null;
  counterparty_display_name?: string | null;
}

/**
 * **Who the sentence is written about** — the party this render leaves implicit.
 *
 * The id is what decides it. Names were, and that was the bug: the extractor
 * writes the spelling a source used ("Shawn Johnson East") while the entity is
 * displayed under its own ("Shawn Johnson"), and comparing the two printed the
 * subject as her own counterparty and flipped every direction word on 27 prod
 * lines — "Married to Shawn Johnson East" on Shawn Johnson's brief.
 *
 * The name is kept for one job it is still the only answer to: a ref that
 * carries **no** resolved id at all, on a payload written before the resolution
 * pre-pass filled them in.
 */
export interface FactReader {
  /** The entity the sentence is about — the dossier subject, or the fact's own subject. */
  id: string | null;
  name: string;
}

function str(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  if (typeof value === 'number') return String(value);
  return null;
}

/** An `EntityRef` is `{name, kind?, entity_id?, anchors?}` — the name is what reads. */
function entityName(value: unknown): string | null {
  if (typeof value === 'string') return str(value);
  if (value && typeof value === 'object' && 'name' in value) {
    return str((value as { name: unknown }).name);
  }
  return null;
}

/**
 * Which of the fact's two entity columns the reader occupies, or null when the
 * ids cannot say.
 *
 * Both columns matter because the record read unions them: `factsForSubject`
 * returns `subject_entity_id = :id OR counterparty_entity_id = :id`, so a pair
 * fact filed under the *other* person is drawn inline on this brief with this
 * subject in the counterparty column. Reading such a row as though the reader
 * were always the fact's subject names the reader as their own counterpart.
 */
type ReaderColumn = 'subject' | 'counterparty' | null;

function readerColumn(fact: FactStatementSource, reader: FactReader): ReaderColumn {
  /* `sameEntityId`, not `===`: a column is what Postgres rendered a `uuid` as,
     always lower-case, while `reader.id` is this dossier's subject id read off
     the URL — and `ParseUUIDPipe` accepts an upper-cased uuid, so an upper-cased
     address is an ordinary 200 whose reader then matches neither column. Null
     here is not a null-shaped fallback: `pairRead` reads it as "the reader is
     the subject", takes the *counterparty* column as the far side, and names the
     reader as their own counterpart — "Child of Shawn Johnson" on Shawn
     Johnson's own brief. (The fold also subsumes the old null and empty-string
     guards, which is why they are gone rather than kept alongside it.) */
  if (sameEntityId(fact.subject_entity_id, reader.id)) return 'subject';
  if (sameEntityId(fact.counterparty_entity_id, reader.id)) return 'counterparty';
  return null;
}

/** A pair fact read from one side: the reader's side of `fields`, and who the other party is. */
interface PairRead {
  /** Which of `fields.a` / `fields.b` the reader is — the direction word turns on this. */
  side: 'a' | 'b';
  /** The counterpart, by their **live** display name wherever the payload allows. */
  other: string | null;
}

function pairRead(fact: FactStatementSource, fields: Fields, reader: FactReader): PairRead {
  const onCounterparty = readerColumn(fact, reader) === 'counterparty';
  /* The far column — the party the sentence names. Note that "far" is a column
     question and "side" is a `fields` question, and they are answered
     separately: a merge swaps the columns to restore `subject = min(pair uuids)`
     and leaves `fields` untouched, so the column says who the other party is and
     `fields.relation` plus the side says what to call them. */
  const otherId = (onCounterparty ? fact.subject_entity_id : fact.counterparty_entity_id) ?? null;
  const otherLive = onCounterparty ? fact.subject_display_name : fact.counterparty_display_name;
  const side = pairSide({ fields, otherId, isSubjectSide: !onCounterparty }, reader.id);
  /* The payload's own spelling is the fallback, not the source: it is what a row
     carrying no live column name has (a pre-columns vintage, or a far side that
     never resolved). It is at least now read off the **computed** side. */
  const other = str(otherLive) ?? entityName(fields[side === 'a' ? 'b' : 'a']);
  return { side, other };
}

/**
 * Is this single-slot ref the reader — the `principal` of a financial event, the
 * `donor` of a gift?
 *
 * Three sources, asked in the order of how much each actually knows:
 *
 *  1. **the ref's own id**, when it names the reader (yes) or names the fact's
 *     *other* live column (no, and just as firmly — that is somebody else on
 *     this very row). An id that names **neither** column is a merged-away
 *     twin's: `entity-merge` repoints the columns and deliberately leaves
 *     `fields` alone, so a stale stamp is precisely the case where the ids have
 *     nothing to say, and it must not be let say something anyway;
 *  2. **the columns**: §7.2c subjects both of these types to their principal —
 *     an org donor takes the subject slot and leaves the person on the
 *     counterparty one — so the reader sitting in the subject column *is* the
 *     principal, and sitting anywhere else is not. An id outranks a spelling
 *     here for the same reason it does in `pairSide`;
 *  3. **the name**, last, and only where the columns cannot place the reader at
 *     all — a row carrying no columns (Flagged findings on a lineage the read
 *     could not enrich), or a reader with no id. Asking it any earlier is the
 *     prod mis-render itself: "Shawn Johnson East · giving to ..." on Shawn
 *     Johnson's brief, six times, off a `donor` ref whose spelling was the
 *     source's and whose id the pre-pass had never stamped.
 */
function refIsReader(fact: FactStatementSource, value: unknown, reader: FactReader): boolean {
  const id = refEntityId(value);
  if (id !== null && reader.id) {
    /* Both probes fold (`sameEntityId`). Three spellings meet on these two lines
       and only one of them is normalized: a ref id is jsonb, whatever the
       resolver stamped; a column is Postgres's lower-case `uuid`; `reader.id`
       came off the URL. Raw, the second line is the sharper of the pair — it
       misses the reader's own id on case, then *matches* that same id in the
       subject column and reads it as "firmly somebody else", which is the prod
       mis-render this function was written against ("Shawn Johnson East · giving
       to …" on Shawn Johnson's brief) reached from the address bar. */
    if (sameEntityId(id, reader.id)) return true;
    if (sameEntityId(id, fact.subject_entity_id) || sameEntityId(id, fact.counterparty_entity_id)) {
      return false;
    }
  }
  const column = readerColumn(fact, reader);
  if (column !== null) return column === 'subject';
  /* The reader has an id and the fact has columns, and the reader is in neither:
     they are not this fact's principal, and the spelling is not asked. */
  if (reader.id && (fact.subject_entity_id || fact.counterparty_entity_id)) return false;
  const name = entityName(value);
  const readerName = reader.name.trim();
  if (name !== null && readerName.length > 0) {
    return name.toLowerCase() === readerName.toLowerCase();
  }
  return false;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Does this name already say the edition?
 *
 * Since catalog v3 an event node is named **edition-qualified** — "Monterey
 * Historics 2024", never the bare series (`facet_extractor@1.8.0`) — while
 * `event_participation.edition` keeps carrying the same running, because the
 * name and the field are read by different things: the name identifies the node,
 * the field keys the fact. Printing both unconditionally is what that costs the
 * reader — "Monterey Historics 2024, 2024" says the year twice — so the clause
 * is dropped when the name already carries it.
 *
 * Whole tokens only. "2024" inside "20240" is not this edition, and a name that
 * merely *contains* the digits is not a name that states them.
 */
function restatesEdition(name: string, edition: string): boolean {
  const fold = (value: string) =>
    ` ${value
      .toLowerCase()
      .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
      .trim()} `;
  const token = fold(edition);
  return token.trim().length > 0 && fold(name).includes(token);
}

/**
 * The seat's own words for the capacity it was held in. Exported because the
 * network panel prints the same enum and must print it the same way — a reader
 * who sees "Advisory board member" on the record row should not meet a bare
 * `advisory` one tab over.
 */
export const BOARD_WORDS: Record<string, string> = {
  member: 'Board member',
  chair: 'Board chair',
  trustee: 'Trustee',
  advisory: 'Advisory board member',
};

/**
 * **How each register's entry reads as a sentence** (catalog v8).
 *
 * `word` is the head the line leads with — the *thing*, before anything the
 * register says about it — and it is a phrase per kind rather than the kind's
 * own name, because "Bar admission" is a column heading and "Admitted to the
 * bar" is a sentence.
 *
 * `place` says whether the head takes the jurisdiction inside it. An admission,
 * a medical licence and a securities registration are all held *somewhere*, and
 * naming the state is half of what the fact says; a specialty board's
 * certification, the patent roster and an NPI are national, and "NPI in US"
 * reads as a mistake. Where the head does not take it, the jurisdiction is
 * simply not printed — it is in the key, and the body's own name carries the
 * scope on every one of those three.
 *
 * `numbered` is the one kind whose head *is* its number: an NPI is the
 * identifier, and "NPI 1972813608" is how the record's own identity line reads
 * it. Everywhere else the number trails as its own clause.
 */
interface LicenseWord {
  readonly word: string;
  readonly place?: boolean;
  readonly numbered?: boolean;
}

/** `other`, and the line a kind this table has never heard of falls back to. */
const LICENSE_WORD_OTHER: LicenseWord = { word: 'License', place: true };

const LICENSE_WORDS: Record<string, LicenseWord> = {
  bar_admission: { word: 'Admitted to the bar', place: true },
  medical_license: { word: 'Medical license', place: true },
  board_certification: { word: 'Board certified' },
  patent_registration: { word: 'Registered with the patent office' },
  npi: { word: 'NPI', numbered: true },
  securities_registration: { word: 'Securities registration', place: true },
  other: LICENSE_WORD_OTHER,
};

/** **A data broker, never which one** (2026-09-26, Soroush): the office reads
    what is listed, not which supplier was asked, the same rule `output_hygiene`
    holds the readings to. `broker` stays in the fields, where the key needs it. */
const BROKER_HEAD = 'Data broker';

/**
 * What the broker's record agreed with the subject on, written out.
 *
 * The line says it because it is the whole of what the office can judge the
 * match by: "matched on name" alone is a namesake until something else agrees,
 * and the record must not let a name-only listing read like a confirmed one.
 */
const BROKER_MATCH_WORDS: Record<string, string> = {
  name: 'name',
  dob: 'date of birth',
  city: 'city',
  state: 'state',
  street: 'street address',
  zip: 'ZIP code',
  employer: 'employer',
  relative: 'a relative',
};

/** "name", "name and city", "name, city and state" — read as a sentence, not a list. */
function matchedWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** `1998–2024`, `since 1998`, `1998` — the span the listed addresses cover. */
function addressSpan(from: number | null, to: number | null): string | null {
  if (from === null && to === null) return null;
  if (from !== null && to !== null) return from === to ? `${from}` : `${from}–${to}`;
  return from !== null ? `since ${from}` : `to ${to}`;
}

function wholeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/**
 * "9 addresses (1998–2024), 3 phones, 2 emails, 5 relatives, 1 employer" — what
 * the broker is selling, as counts.
 *
 * A count of zero is left out rather than printed: the office reads this to
 * know what is purchasable, and "0 emails" beside four real counts is a line
 * about nothing. A record that listed the person and nothing else returns
 * `null`, which the caller renders as the listing alone.
 */
function brokerCounts(fields: Fields): string | null {
  const addresses = wholeNumber(fields['addresses_count']) ?? 0;
  const span = addressSpan(
    wholeNumber(fields['addresses_from']),
    wholeNumber(fields['addresses_to']),
  );
  const parts = [
    addresses > 0
      ? `${plural(addresses, 'address', 'addresses')}${span ? ` (${span})` : ''}`
      : null,
    ...(
      [
        ['phones_count', 'phone', 'phones'],
        ['emails_count', 'email', 'emails'],
        ['relatives_count', 'relative', 'relatives'],
        ['employers_count', 'employer', 'employers'],
      ] as const
    ).map(([key, one, many]) => {
      const count = wholeNumber(fields[key]) ?? 0;
      return count > 0 ? plural(count, one, many) : null;
    }),
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** How a residence's stated value reads: "sold for $8.2 million in 2019". */
const RESIDENCE_VALUE_LEADS: Record<string, string> = {
  sale: 'sold for',
  assessed: 'assessed at',
  listed: 'listed at',
  estimate: 'estimated at',
};

function residenceValueClause(fields: Fields): string | null {
  const value = str(fields['value']);
  if (!value) return null;
  const lead = RESIDENCE_VALUE_LEADS[str(fields['value_kind']) ?? ''] ?? 'valued at';
  /* The year only where the date leads with one: intake is not told the ISO
     form, and "March 2019" must not read as "in Marc". */
  const year = /^\d{4}/.exec(str(fields['value_date']) ?? '')?.[0];
  return year ? `${lead} ${value} in ${year}` : `${lead} ${value}`;
}

/** The value band an on-chain exposure reading holds (spec decision 10.3): a band, never a figure. */
const ONCHAIN_VALUE_BANDS: Record<string, string> = {
  under_100k: 'under $100K',
  '100k_to_1m': '$100K to $1M',
  '1m_to_10m': '$1M to $10M',
  over_10m: 'over $10M',
};

/**
 * What the read of their known wallets showed, as counts and a band: the half
 * of an on-chain exposure reading that runs only when wallets are on record.
 */
function onchainWalletClauses(fields: Fields): (string | null)[] {
  if (fields['wallets_read'] !== true) return [];
  const verified = wholeNumber(fields['wallets_verified']) ?? 0;
  const predicted = wholeNumber(fields['wallets_predicted']) ?? 0;
  const supplied = wholeNumber(fields['wallets_supplied']) ?? 0;
  const band =
    typeof fields['value_band'] === 'string'
      ? ONCHAIN_VALUE_BANDS[fields['value_band']]
      : undefined;
  const from = wholeNumber(fields['history_from']);
  const counterparties = wholeNumber(fields['counterparties_named']);
  const exchanges = wholeNumber(fields['exchanges_used']);
  return [
    verified > 0 ? `${plural(verified, 'wallet', 'wallets')} attributed to them (verified)` : null,
    predicted > 0 ? `${plural(predicted, 'wallet', 'wallets')} predicted to be theirs` : null,
    supplied > 0 ? `${plural(supplied, 'wallet', 'wallets')} you supplied` : null,
    band ? `visible value ${band}` : null,
    from ? `history since ${from}` : null,
    counterparties
      ? `${plural(counterparties, 'named counterparty', 'named counterparties')}`
      : null,
    exchanges ? `${plural(exchanges, 'exchange', 'exchanges')} used` : null,
  ];
}

/** "$1.9M", "$350K", "$950": a whole-dollar figure at the grain a reader compares. */
function usdShort(value: unknown): string | null {
  const n = wholeNumber(value);
  if (n === null) return null;
  const at = (div: number, unit: string) => {
    const v = n / div;
    return `$${v >= 100 ? Math.round(v) : Number(v.toFixed(1))}${unit}`;
  };
  if (n >= 1_000_000_000) return at(1_000_000_000, 'B');
  if (n >= 1_000_000) return at(1_000_000, 'M');
  if (n >= 1_000) return at(1_000, 'K');
  return `$${n}`;
}

/** What ties the counted wallets to the subject, in the office's terms. */
const HOLDING_BASIS: Record<string, string> = {
  supplied: 'supplied by you',
  verified: 'attributed to them (verified)',
  supplied_and_verified: 'supplied by you and attributed to them (verified)',
};

const CHECK_WORDS: Record<string, string> = {
  liens: 'Liens and judgments',
  criminal: 'Criminal records',
  bankruptcy: 'Bankruptcy filings',
  regulatory: 'Regulatory records',
  adverse_media: 'Adverse media',
};

/**
 * "1996 to 2003" / "since 2003" — the window, in the statement itself.
 *
 * The server's `window` first, and the fields behind it, because this line is
 * rendered in two places that are not the same place: a **fact** carries the
 * window the ingest derived, and a **review card** is a claim that has not been
 * ingested yet and carries none. Reading the fields is what keeps the office
 * from being asked to approve a license, a job or a membership whose dates the
 * card silently dropped — the one thing a reviewer most needs to see.
 *
 * `issued`/`expires` are here for `professional_license`, beside
 * `start`/`since` and `end`/`until`, which is every window-shaped pair of field
 * names the catalog uses.
 */
function windowClause(fact: FactStatementSource, fields: Fields): string | null {
  const start =
    fact.window?.start_date ??
    str(fields['start']) ??
    str(fields['since']) ??
    str(fields['issued']);
  const end =
    fact.window?.end_date ?? str(fields['end']) ?? str(fields['until']) ?? str(fields['expires']);
  if (start && end) return `${formatLoose(start)} to ${formatLoose(end)}`;
  if (start) return `since ${formatLoose(start)}`;
  if (end) return `until ${formatLoose(end)}`;
  return null;
}

function relationLine(
  fact: FactStatementSource,
  fields: Fields,
  reader: FactReader,
): string | null {
  const relation = str(fields['relation']);
  const { side, other } = pairRead(fact, fields, reader);
  if (!relation || !other) return null;
  // The catalog's direction: `relation` is what a is to b, so b reads its inverse.
  if (isFamilyRelation(relation)) {
    return `${capitalize(familyRelationFrom(relation, side === 'a' ? 'a' : 'b').phrase)} ${other}`;
  }
  return `${capitalize(relation)}: ${other}`;
}

function isOrgRelation(value: string | null): value is OrgRelation {
  return value !== null && (ORG_RELATIONS as readonly string[]).includes(value);
}

/**
 * **What the fact says read from the subject's own page**, one arm per relation.
 * The other name is the counterparty's. `hosted` is the one that reads forwards
 * — the subject is the host, and what it hosted is the event.
 */
const FROM_THE_SUBJECT: Record<OrgRelation, (counterparty: string) => string> = {
  part_of: (counterparty) => `Part of ${counterparty}`,
  governed_by: (counterparty) => `Governed by ${counterparty}`,
  affiliated_with: (counterparty) => `Affiliated with ${counterparty}`,
  hosted: (counterparty) => `Hosted ${counterparty}`,
  sponsored: (counterparty) => `Sponsored ${counterparty}`,
};

/**
 * **And read from the counterparty's page** — the whole's, the governing body's,
 * or, on a `hosted`, the event's own. The other name is the subject's.
 */
const FROM_THE_COUNTERPARTY: Record<OrgRelation, (subject: string) => string> = {
  part_of: (subject) => `${subject} is part of this organization`,
  governed_by: (subject) => `Governs ${subject}`,
  affiliated_with: (subject) => `Affiliated with ${subject}`,
  hosted: (subject) => `Hosted by ${subject}`,
  sponsored: (subject) => `Sponsored by ${subject}`,
};

/**
 * **One organisation standing inside or under another — or the event it put on —
 * read from whichever of the two the page is about.**
 *
 * The slots are fixed (`common/catalog/fact-types.ts`): `org` — the fact's
 * subject — is the smaller or the governed side on three of the four relations
 * and the *host* on `hosted`, and `counterparty` is the whole, the governing
 * body, or the event. Fixed either way, which is what makes the line a direction
 * question rather than a per-relation one: the same two columns say which side
 * the reader is on, and the relation only chooses the words.
 *
 * The reader really can be either side. `factsForSubject` unions both columns,
 * the knowledge panel re-centres on the fact's subject *except* where that
 * subject has been shredded and has no name to lead with — at which point the
 * counterparty's own panel is reading a row it sits in the second column of —
 * and an event's page is always reading one. A line written as though the reader
 * were always the subject would tell the University of Texas System that it is
 * part of itself, and tell Medici Day that it hosted Medici Day.
 *
 * Where the ids place the reader in neither column — a Flagged finding carrying
 * no lineage — the sentence names both sides instead of guessing
 * (`orgRelationSentence`, the same string the merge card promised and the server
 * recorded for the three relations that card can write).
 */
function orgRelationLine(
  fact: FactStatementSource,
  fields: Fields,
  reader: FactReader,
): string | null {
  const relation = str(fields['relation']);
  if (!isOrgRelation(relation)) return null;
  /* The live column name first and the payload's own spelling second — the same
     order `pairRead` asks in, and for the same reason: the row's spelling is
     what a source wrote, the column is what the record calls the node now. */
  const subject = str(fact.subject_display_name) ?? entityName(fields['org']);
  const counterparty = str(fact.counterparty_display_name) ?? entityName(fields['counterparty']);

  const column = readerColumn(fact, reader);
  const head =
    column === 'subject'
      ? counterparty && FROM_THE_SUBJECT[relation](counterparty)
      : column === 'counterparty'
        ? subject && FROM_THE_COUNTERPARTY[relation](subject)
        : subject && counterparty && orgRelationSentence(relation, subject, counterparty);
  if (!head) return null;

  /* `detail` is the catalog's "as the source put it", and the office's own
     button files the very sentence this line is already saying — so appending it
     unconditionally would print the relation twice, once in the reader's
     direction and once in the record's. It is appended when it adds something
     and dropped when it is that sentence again. */
  const detail = str(fields['detail']);
  const restated =
    detail !== null &&
    subject !== null &&
    counterparty !== null &&
    fold(detail) === fold(orgRelationSentence(relation, subject, counterparty));
  return joinClauses([head, restated ? null : detail]);
}

/** Case and trailing punctuation set aside — two spellings of one sentence. */
function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[.\s]+$/u, '');
}

/** The last resort: the payload's own scalars, in the order they arrived. */
function fallbackLine(fields: Fields): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(fields)) {
    const named = entityName(value) ?? str(value);
    if (named) parts.push(key === 'prose' ? named : `${named}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No readable detail';
}

/**
 * An ISO 3166-1 alpha-2 code as the country's English name, or the code itself
 * where the platform has no name for it.
 *
 * **What `fallback: 'none'` does and does not buy.** It returns `undefined` for
 * a code CLDR has no entry for at all (`QQ`, `AA`, the `QM`–`QZ` private
 * range), which is the case this falls through on. It does **not** filter the
 * codes CLDR knows but nobody holds a citizenship of: `ZZ` is a known code
 * whose name is literally "Unknown Region", `XA`/`XB` are ICU's pseudo-locale
 * test regions, and `EU`/`UN`/`QO` name a union, an organization and a
 * statistical grouping. Every one of them satisfies a two-letter check.
 *
 * Deciding which two-letter strings are countries is the **catalog's** job at
 * the write boundary and it does it there (`iso-countries.ts` refuses every one
 * of those, and corrects `UK` to `GB`), so none of them can reach this function
 * from a fact written since catalog v10. The tolerance below is for what a
 * renderer is actually handed — opaque jsonb, and rows older than the rule —
 * and not a second, weaker copy of it: a row this hid would still be on the
 * record. What it owes the office is that a code it cannot name honestly
 * reaches them as the code.
 */
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });

/**
 * Where CLDR's label is a disambiguator rather than the country's name. Each is
 * right as an entry in a region list and wrong as the value on a line in an
 * office brief — "Congo - Kinshasa" carries a spaced hyphen mid-line, and
 * "Myanmar (Burma)" is a gloss nobody asked for.
 */
const COUNTRY_NAME_OVERRIDES: Record<string, string> = {
  CD: 'Democratic Republic of the Congo',
  CG: 'Republic of the Congo',
  MM: 'Myanmar',
  HK: 'Hong Kong',
  MO: 'Macao',
  PS: 'Palestinian Territories',
};

function countryName(code: string): string {
  const upper = code.toUpperCase();
  const override = COUNTRY_NAME_OVERRIDES[upper];
  if (override) return override;
  try {
    return REGION_NAMES.of(upper) ?? upper;
  } catch {
    /* `of` throws on anything that is not two letters or three digits, and the
       catalog's regex means that cannot reach a stored fact — but this renders
       whatever the wire hands it, and a thrown error would take the whole
       record page down to avoid printing one bad row. */
    return upper;
  }
}

/**
 * What `nationality.basis` adds to the line, and the one value that adds
 * nothing.
 *
 * `stated` renders as no clause at all: every fact on this record is something
 * a source stated, so the word carries no information here and would read as a
 * hedge against the fact beside it — "Citizenship, Ireland · stated" invites the
 * question the record has better answers to (the tier and the corroboration
 * count, both already on the row). A passport and an official record are
 * genuinely different readings and say so.
 */
const BASIS_WORDS: Record<NationalityBasis, string | null> = {
  passport: 'passport',
  official_record: 'official record',
  /* Named with a null rather than left out, so the omission is a decision the
     table states and a fourth basis cannot arrive without this map failing to
     compile. A `Record<string, string>` missing a member renders as nothing,
     with no type error and no failing test — which is the shape the server-side
     mirror got a fence for (`fact-type-fields.spec.ts`) and this one did not. */
  stated: null,
};

const basisClause = (basis: string | null): string | null =>
  (basis && BASIS_WORDS[basis as NationalityBasis]) ?? null;

/** One sentence per fact, in the reader's direction. */
export function factLine(fact: FactStatementSource, reader: FactReader): string {
  const fields = fact.fields as Fields;
  const window = windowClause(fact, fields);

  switch (fact.fact_type) {
    case 'vitals': {
      const born = str(fields['born']);
      const place = str(fields['birthplace']);
      if (born && place) return `Born ${formatLoose(born)}, ${place}`;
      if (born) return `Born ${formatLoose(born)}`;
      if (place) return `Born in ${place}`;
      return fallbackLine(fields);
    }
    case 'education': {
      const institution = entityName(fields['institution']);
      const degree = str(fields['degree']);
      const year = str(fields['year']);
      const head = degree && institution ? `${degree}, ${institution}` : (institution ?? degree);
      if (!head) return fallbackLine(fields);
      return year ? `${head}, ${formatLoose(year)}` : head;
    }
    /**
     * **What the person is licensed to do, and what the register says about it**
     * (catalog v8). It renders beside `education` — a credential reads next to
     * the degree — and it is built the way the rest of this file builds a line:
     * the thing first, then the words the source used, each clause dropped when
     * the field behind it is absent.
     *
     * The status is the clause that matters, so it is the one that takes the
     * date: "active since 2004" says in three words what a status and a separate
     * window say in six, and it is the sentence the office reads on a lawyer.
     * Where there is no status the ordinary window clause answers instead, and
     * where there is a status *and* an expiry the expiry trails as its own
     * clause rather than being lost to the merge.
     *
     * `as_of` is deliberately not in the line: it dates the *reading* rather
     * than the licence, and that is the `.src` line's job (`factDating`), where
     * it reads the same way a records check's does.
     */
    case 'professional_license': {
      const kind = str(fields['kind']);
      if (!kind) return fallbackLine(fields);
      const spec = LICENSE_WORDS[kind] ?? LICENSE_WORD_OTHER;
      const jurisdiction = str(fields['jurisdiction']);
      const identifier = str(fields['identifier']);
      const placed = spec.place && jurisdiction ? `${spec.word} in ${jurisdiction}` : spec.word;
      const head = spec.numbered && identifier ? `${placed} ${identifier}` : placed;
      const status = str(fields['status']);
      const issued = fact.window?.start_date ?? str(fields['issued']);
      const expires = fact.window?.end_date ?? str(fields['expires']);
      return joinClauses([
        head,
        str(fields['credential']),
        str(fields['authority']),
        spec.numbered ? null : identifier,
        status && issued ? `${status} since ${formatLoose(issued)}` : status,
        status && issued ? (expires ? `expires ${formatLoose(expires)}` : null) : window,
      ]);
    }
    case 'employment': {
      const org = entityName(fields['org']);
      if (!org) return fallbackLine(fields);
      const founder = fields['founder'] === true;
      const role = str(fields['role']);
      const head = founder ? `Founded ${org}` : org;
      return joinClauses([role ? `${head}, ${role}` : head, window]);
    }
    case 'board_seat': {
      const org = entityName(fields['org']);
      if (!org) return fallbackLine(fields);
      const role = str(fields['role']);
      const word = (role && BOARD_WORDS[role]) ?? 'Board member';
      return joinClauses([`${word}, ${org}`, window]);
    }
    case 'association_membership': {
      const org = entityName(fields['org']);
      if (!org) return fallbackLine(fields);
      return joinClauses([`Member, ${org}`, window]);
    }
    case 'family_relation': {
      const line = relationLine(fact, fields, reader);
      return line ?? fallbackLine(fields);
    }
    case 'residence': {
      const place = str(fields['place']);
      if (!place) return fallbackLine(fields);
      const kind = str(fields['kind']);
      const holding = str(fields['holding']);
      const head =
        kind === 'primary'
          ? `Primary residence, ${place}`
          : kind === 'secondary'
            ? `Second residence, ${place}`
            : `Residence, ${place}`;
      return joinClauses([
        head,
        holding === 'trust' ? 'held in trust' : null,
        residenceValueClause(fields),
      ]);
    }
    /**
     * **Citizenship, <country>** (catalog v10), one line per citizenship — a dual
     * national has two facts and reads as two rows, which is what the catalog's
     * key already decided.
     *
     * The country is printed by name and stored as a code, and the widening
     * happens here rather than on the record: `country_iso2` is what a reader
     * *joins* on (the travel brief, when it is built, matches it against a
     * consulate table), and a name in the field would be a join against prose.
     * `Intl.DisplayNames` is the table — the platform already ships every
     * country's English name, and a 249-row map in this file would be a second
     * copy to maintain and to translate.
     *
     * **`Citizenship, X` and not `Citizen of X`**, which is the head-then-value
     * shape `residence` and `financial_event` already read in ("Primary
     * residence, Point Loma"). The sentence form was the first draft and it is
     * wrong for about thirty countries: CLDR answers with *labels*, so it
     * produced "Citizen of United States" and "Citizen of Netherlands" — the
     * two likeliest values in the whole type reading worst. The alternative was
     * a table of which countries take a definite article, which is an English
     * grammar to maintain beside a list of countries. The head form needs
     * neither and sounds like the rest of the record.
     */
    case 'nationality': {
      const code = str(fields['country_iso2']);
      if (!code) return fallbackLine(fields);
      return joinClauses([`Citizenship, ${countryName(code)}`, basisClause(str(fields['basis']))]);
    }
    case 'financial_event': {
      const kind = str(fields['kind']);
      const org = entityName(fields['org']);
      const counterparty = entityName(fields['counterparty']);
      const magnitude = str(fields['magnitude']);
      const date = str(fields['date']);
      const principal = entityName(fields['principal']);
      const head = kind ? capitalize(kind) : 'Financial event';
      const subjectIsPrincipal = refIsReader(fact, fields['principal'], reader);
      return joinClauses([
        org ? `${head}, ${org}` : !subjectIsPrincipal && principal ? `${head}, ${principal}` : head,
        counterparty ? `with ${counterparty}` : null,
        magnitude,
        date ? formatLoose(date) : null,
      ]);
    }
    /**
     * **Who put money into which company** (catalog v7), read from whichever of
     * the two the page is about.
     *
     * The reader really can be either: the investor's own record carries the
     * fact in its subject column, the company's knowledge panel carries it in the
     * counterparty column, and `factsForSubject` unions both. So the direction is
     * a column question exactly as `org_relation`'s is — from the investor the
     * company is named, from the company the investor is, and where the ids place
     * the reader in neither column (a flagged finding with no lineage) the
     * sentence names both sides rather than guessing.
     *
     * The clauses after the head are the fact's own words, in the order a reader
     * asks them in, and `role` prints only when it is `lead`: a round with no
     * stated lead would otherwise label every name in it "participant", which
     * says less than nothing and takes room to say it.
     */
    case 'investment': {
      const company = str(fact.counterparty_display_name) ?? entityName(fields['org']);
      const investor = str(fact.subject_display_name);
      const column = readerColumn(fact, reader);
      const head =
        column === 'subject'
          ? company && `Invested in ${company}`
          : column === 'counterparty'
            ? investor && `${investor} invested`
            : investor && company && `${investor} invested in ${company}`;
      if (!head) return fallbackLine(fields);
      const date = str(fields['date']);
      return joinClauses([
        head,
        str(fields['round']),
        str(fields['amount']),
        date ? formatLoose(date) : null,
        fields['role'] === 'lead' ? 'lead' : null,
        str(fields['detail']),
      ]);
    }
    /**
     * **What a subject's known wallets held** (catalog v13) — "these wallets",
     * never a total. The predicted wallets are their own clause, valued apart,
     * so nothing a reader adds up mixes them in. No provider is named: the
     * analysis does not name its sources (on-chain spec §3).
     */
    case 'digital_asset_holding': {
      const visible = usdShort(fields['visible_usd']);
      const counted = wholeNumber(fields['wallets_counted']);
      if (visible === null || counted === null) return fallbackLine(fields);
      const chains = str(fields['chains']);
      const basis =
        typeof fields['basis'] === 'string' ? HOLDING_BASIS[fields['basis']] : undefined;
      const borrowed = usdShort(fields['borrowed_usd']);
      const from = wholeNumber(fields['history_from']);
      const peak = usdShort(fields['peak_usd']);
      const peakYear = wholeNumber(fields['peak_year']);
      const predictedCount = wholeNumber(fields['wallets_predicted']) ?? 0;
      const predicted = usdShort(fields['predicted_usd']);
      return joinClauses([
        `${visible} across ${plural(counted, 'known wallet', 'known wallets')}` +
          (chains ? ` (${chains})` : ''),
        basis ?? null,
        borrowed ? `${borrowed} borrowed against them` : null,
        from ? `history since ${from}` : null,
        peak ? `peak ${peak}${peakYear ? ` in ${peakYear}` : ''}` : null,
        predictedCount > 0
          ? `${plural(predictedCount, 'more wallet', 'more wallets')} predicted` +
            (predicted ? `, ${predicted}, not included` : ', not included')
          : null,
      ]);
    }
    case 'litigation': {
      const caseName = str(fields['case_name']);
      const court = str(fields['court']);
      const filed = str(fields['filed']);
      const role = str(fields['subject_role']);
      const matter = str(fields['matter']);
      const status = str(fields['case_status']);
      if (!caseName) return fallbackLine(fields);
      const venue = joinClauses([court, filed ? formatLoose(filed) : null]).replace(' · ', ', ');
      return joinClauses([
        venue ? `${caseName} (${venue})` : caseName,
        role ? `as ${role}` : null,
        matter,
        status,
      ]);
    }
    case 'records_check': {
      const check = str(fields['check']);
      const scope = str(fields['scope']);
      const result = str(fields['result']);
      const head = (check && CHECK_WORDS[check]) ?? 'Records check';
      return joinClauses([scope ? `${head}, ${scope}` : head, result]);
    }
    /**
     * **What a data broker is selling about this person** (catalog v9) — what
     * it answered, the counts, and what the record matched on.
     *
     * No value is rendered because none is stored: the counts and the address
     * span are the whole fact, and a line that printed a city would be the one
     * place in the product where a broker's address history became text the
     * office reads. `as_of` is not in the statement for the same reason it is
     * not in a records check's — it dates the *reading*, which is the `.src`
     * line's job (`factDating`).
     */
    case 'broker_exposure': {
      if (!str(fields['broker'])) return fallbackLine(fields);
      const head = BROKER_HEAD;
      const basis = Array.isArray(fields['match_basis'])
        ? (fields['match_basis'] as unknown[])
            .map((kind) => (typeof kind === 'string' ? (BROKER_MATCH_WORDS[kind] ?? kind) : null))
            .filter((word): word is string => word !== null)
        : [];
      switch (fields['outcome']) {
        case 'not_listed':
          return joinClauses([head, 'no matching record']);
        case 'ambiguous':
          return joinClauses([
            head,
            'more than one possible record, none of them confirmed as this person',
          ]);
        case 'listed':
          return joinClauses([
            head,
            'listed',
            brokerCounts(fields),
            basis.length > 0 ? `matched on ${matchedWords(basis)}` : null,
          ]);
        default:
          return fallbackLine(fields);
      }
    }
    /**
     * What a stranger can attribute to them on-chain, from their name or a
     * wallet. Never which provider, never an address and never a figure: the
     * value is a band, and the figure stays in the evidence.
     */
    case 'onchain_exposure': {
      const matches = wholeNumber(fields['name_search_matches']) ?? 0;
      const findsThem = fields['name_search_finds_them'] === true;
      const search =
        matches === 0
          ? 'a search of their name finds no on-chain identity'
          : findsThem
            ? `a search of their name finds their on-chain identity` +
              (matches > 1 ? ` among ${matches}` : '')
            : `a search of their name finds ${plural(matches, 'on-chain identity', 'on-chain identities')}` +
              (fields['outcome'] === 'ambiguous' ? '' : ', none confirmed as theirs');
      switch (fields['outcome']) {
        case 'attributed':
          return joinClauses([
            'On-chain: attributed to them',
            search,
            ...onchainWalletClauses(fields),
          ]);
        case 'ambiguous':
          return joinClauses([
            'On-chain: possible identities, none confirmed as theirs',
            search,
            ...onchainWalletClauses(fields),
          ]);
        case 'not_attributed':
          return joinClauses([
            'On-chain: nothing attributed to them',
            search,
            ...onchainWalletClauses(fields),
          ]);
        default:
          return fallbackLine(fields);
      }
    }
    /**
     * Which accounts the office's own identifiers open. Counts and categories,
     * never an account, a username or which platform.
     */
    case 'account_exposure': {
      const checked = wholeNumber(fields['identifiers_checked']) ?? 0;
      const supplied = plural(checked, 'identifier you supplied', 'identifiers you supplied');
      switch (fields['outcome']) {
        case 'accounts_found': {
          const accounts = wholeNumber(fields['accounts_found']) ?? 0;
          const opening = wholeNumber(fields['identifiers_with_accounts']) ?? 0;
          const kinds = Array.isArray(fields['kinds_with_accounts'])
            ? (fields['kinds_with_accounts'] as unknown[]).filter(
                (kind): kind is string => typeof kind === 'string',
              )
            : [];
          const categories = Array.isArray(fields['categories'])
            ? (fields['categories'] as unknown[]).filter(
                (category): category is string => typeof category === 'string',
              )
            : [];
          return joinClauses([
            `Accounts: ${plural(accounts, 'account', 'accounts')} opened by ${opening} of ${supplied}`,
            kinds.length > 0 ? `through ${kinds.join(', ')}` : null,
            categories.length > 0 ? categories.join(', ') : null,
          ]);
        }
        case 'none_found':
          return `Accounts: none found for ${supplied}`;
        default:
          return fallbackLine(fields);
      }
    }
    case 'interest': {
      const interest = str(fields['interest']);
      if (!interest) return fallbackLine(fields);
      return joinClauses([capitalize(interest), str(fields['detail'])]);
    }
    case 'event_participation': {
      const event = entityName(fields['event']);
      const edition = str(fields['edition']);
      if (!event) return fallbackLine(fields);
      /* `role` is rendered because it is **keyed** (catalog v3): covering an
         edition for a network and attending it with family are two facts, and
         two rows that read identically are two rows the office cannot tell
         apart. It trails the event for the same reason `employment` puts the
         role after the org — the capacity is what the second line is saying
         that the first one is not. */
      const role = str(fields['role']);
      /* The edition is stated once. Under v3 the event is named
         edition-qualified, so `${event}, ${edition}` prints the year twice for
         every fact the current extractor writes (`restatesEdition`). */
      const statesEdition = edition !== null && restatesEdition(event, edition);
      const head = edition && !statesEdition ? `${event}, ${edition}` : event;
      return joinClauses([role ? `${head}, ${role}` : head, str(fields['detail'])]);
    }
    case 'achievement': {
      const what = str(fields['what']);
      if (!what) return fallbackLine(fields);
      const date = str(fields['date']);
      return joinClauses([
        date ? `${capitalize(what)}, ${formatLoose(date)}` : capitalize(what),
        str(fields['context']),
      ]);
    }
    case 'philanthropy': {
      const donor = entityName(fields['donor']);
      const recipient = entityName(fields['recipient']) ?? str(fields['cause']);
      /* Whether the line names a donor at all. The reader gives to things —
         "Giving to X" — and prefixing their own name to that is the mis-render
         prod showed six times ("Shawn Johnson East · giving to ..."), which is
         also why the id is asked before the spelling. An org donor, which §7.2c
         subjects to the org, still earns the prefix: the person's record reads
         that fact back through the org-context union, and whose gift it was is
         the whole content of the row. */
      const donorIsSubject = refIsReader(fact, fields['donor'], reader);
      /* Built lowercase-first and capitalized only where the clause leads the
         line: lowercasing a finished head takes the recipient's own capitals
         down with it ("· giving to wounded warrior project"). */
      const gift = recipient ? `giving to ${recipient}` : 'giving';
      const head = donorIsSubject || !donor ? capitalize(gift) : `${donor} · ${gift}`;
      return joinClauses([head, str(fields['detail']), window]);
    }
    case 'social_tie': {
      const { other } = pairRead(fact, fields, reader);
      const context = str(fields['context']);
      if (!other) return fallbackLine(fields);
      return joinClauses([`Known to ${other}`, context, window]);
    }
    case 'org_relation': {
      return orgRelationLine(fact, fields, reader) ?? fallbackLine(fields);
    }
    /**
     * **What a source said the organisation is** (catalog v6). The record page
     * draws this fact as its own block; here it is one row among the rest, so
     * the description leads and the stated attributes trail it as clauses.
     *
     * `location` and `jurisdiction` are deliberately *not* run through the
     * record's "registered in" phrasing: this line is read beside the
     * organisation's confirmed anchors on the knowledge panel, where the whole
     * question is what a source said as against what the office stands behind,
     * and a clause that rephrases is a clause that has been interpreted.
     */
    case 'org_profile': {
      const description = str(fields['description']);
      if (!description) return fallbackLine(fields);
      const founded = str(fields['founded']);
      return joinClauses([
        description,
        str(fields['sector']),
        str(fields['location']),
        str(fields['jurisdiction']),
        founded ? `founded ${formatLoose(founded)}` : null,
        str(fields['size']),
        str(fields['website']),
      ]);
    }
    /* An event's account of itself, the same clause order as an organization's. */
    case 'event_profile': {
      const description = str(fields['description']);
      if (!description) return fallbackLine(fields);
      const starts = str(fields['starts_on']);
      const ends = str(fields['ends_on']);
      return joinClauses([
        description,
        str(fields['format']),
        starts
          ? ends && ends !== starts
            ? `${formatLoose(starts)} to ${formatLoose(ends)}`
            : formatLoose(starts)
          : null,
        str(fields['venue']),
        str(fields['place']),
        str(fields['expected_size']),
        str(fields['website']),
      ]);
    }
    case 'finding': {
      return str(fields['prose']) ?? fallbackLine(fields);
    }
    default:
      return fallbackLine(fields);
  }
}

/**
 * Types whose truth continues until something ends it. Rule §3.8: these render
 * "last confirmed <date>" and never "current".
 */
const CONTINUING_TYPES: ReadonlySet<FactType> = new Set<FactType>([
  'employment',
  'board_seat',
  'association_membership',
  'residence',
  'philanthropy',
  'social_tie',
  'family_relation',
  'interest',
  /* A campus does not stop being a campus on a date anybody records. The
     relation stands until something ends it, exactly as a board seat or a
     membership does, so it dates "last confirmed" and never "current". */
  'org_relation',
  /* A licence with no expiry stated stands until a register says otherwise — the
     same shape as a membership. Where the register *did* state an expiry the
     window answers first and this set is never consulted. */
  'professional_license',
  /* A citizenship stands until it is renounced or revoked, and nothing the
     record holds dates either. So it reads "last confirmed <date>" — which is
     the honest claim, since what was checked is that a source still said so —
     and never "current". */
  'nationality',
]);

/**
 * The honest-dating clause for the `.src` line. Records checks read "as of";
 * open-ended affiliations read "last confirmed"; a one-off event carries its
 * date in the statement and claims no recency here.
 */
export function factDating(fact: Fact): string | null {
  const fields = fact.fields as Fields;
  /* Two types date the *reading* rather than the thing read, and both answer
     "as of" or nothing at all: a broker listing is a snapshot of what one
     provider held on one day, and a month-old reading that dated itself "last
     confirmed" would claim a currency nobody checked. */
  if (
    fact.fact_type === 'records_check' ||
    fact.fact_type === 'broker_exposure' ||
    fact.fact_type === 'onchain_exposure' ||
    fact.fact_type === 'account_exposure' ||
    fact.fact_type === 'digital_asset_holding'
  ) {
    const asOf = str(fields['as_of']) ?? fact.last_corroborated;
    return asOf ? `as of ${formatMonth(asOf)}` : null;
  }
  /* A licence carries the same `as_of` a check does — when the register was
     read — and a status a year old should say so. Unlike a check it falls
     through when the register did not date itself, because the licence still
     stands: the open-ended rule below then dates the *record's* last
     confirmation, which is the honest answer where the register gave none. */
  if (fact.fact_type === 'professional_license') {
    const asOf = str(fields['as_of']);
    if (asOf) return `as of ${formatMonth(asOf)}`;
  }
  const openEnded = fact.window ? !fact.window.end_date : CONTINUING_TYPES.has(fact.fact_type);
  if (openEnded && fact.last_corroborated) {
    return `last confirmed ${formatMonth(fact.last_corroborated)}`;
  }
  return null;
}

/** Corroboration is a count of independent roots, qualitative and never a score. */
export function corroborationClause(fact: Fact): string | null {
  if (fact.corroboration_count <= 1) return null;
  return plural(fact.corroboration_count, 'source', 'sources');
}

/** The one stamp a row's severity earns, and the ink it wears. */
export interface SeverityChip {
  readonly label: 'Clear' | 'Severe' | 'Moderate' | 'Note';
  /**
   * A `.tag-faint` modifier from `app.css` — the palette is never inlined here.
   *
   * `null` is the app's existing **neutral** stamp: bare `.tag-faint`, the same
   * uncoloured treatment `sensitive`, `pending review` and the section counts
   * already wear. It is a real choice and not a missing one — `Note` says the
   * check recorded something, and colouring it would say the something has a
   * valence, which is exactly what `note` exists to deny. No new palette value
   * was invented for it, because the neutral one was already there.
   */
  readonly ink: 'clear-ink' | 'red-ink' | 'amber-ink' | null;
}

/**
 * **What a row says about its own severity.** Pure, and exported on its own so
 * the ladder can be tested without a render — the discipline `attention.ts`
 * keeps for the same reason.
 *
 * The rule, in the order it is read:
 *
 *  1. **A removed row says nothing.** Greyed and labelled is the whole story;
 *    no treatment speaks over it.
 *  2. **`none` on a `records_check` is an explicit green `Clear`**, everywhere
 *    the row is drawn. This is the positive statement the record owed the
 *    reader: a search that ran and found nothing is news, and leaving it silent
 *    made a clean screen indistinguishable from one nobody ran.
 *
 *    **The word is scoped to the check, and the scope is the point.** Two types
 *    reach `none` — `records_check` off `outcome`, `litigation` off
 *    `subject_role` — and the sentence "clear" is only true of one of them. A
 *    check is a *search*, and "clear" says the search came back empty. A
 *    litigation row is a named case, and stamping `Clear` beside a case caption
 *    says the case came out clean, which is a verdict nobody entered: what
 *    `none` actually means there is that the subject was a witness or otherwise
 *    not a party, and the row's own line already says so. `attention.ts` draws
 *    the same line for the same reason — `cleanChecks` rests the "legal clear"
 *    clause on `records_check` alone — and the row must not say on the page what
 *    the block above it refuses to say in prose.
 *  3. **`info` is a neutral `Note`, everywhere the row is drawn** — the same
 *    always-on rule `Clear` takes, and for the mirror-image reason. `info` is
 *    the one class that never reaches "Anything to know" (`attention.ts` gathers
 *    `severe` and `moderate` and nothing else, deliberately), so a stamp gated
 *    on `severityStamp` would be a stamp that never renders. The word is
 *    uncoloured on purpose: `note` means the check recorded something with no
 *    valence — the subject's own public self-disclosure — and ink of any colour
 *    would give it one.
 *
 *    **Unscoped, where `Clear` is scoped, and the asymmetry is the argument.**
 *    `Clear` had to be pinned to `records_check` because a second type reaches
 *    `none` and the *sentence* "clear" is false of it: beside a case caption it
 *    reads as a verdict. `Note` makes no claim about a search or an outcome — it
 *    says this row is on the record and is not adverse — so there is no type it
 *    could be false of. Two types compute `info` now: `records_check` through
 *    its outcome ladder, and — since the adviser-lookup note's §6 C4 was ruled on
 *    2026-09-14 — `litigation`, whenever its whole `case_status` is a disposition
 *    the forum closed against the claim (`denied`, `dismissed`, `withdrawn`, …).
 *    That is the second type this rule was written to stay honest for, and it is
 *    exactly the case the unscoped wording is right about: `Note` beside a denied
 *    arbitration says the row is on the record and is not adverse, which is true,
 *    where `Clear` beside it would be a verdict on a search nobody ran.
 *  4. **`severe` and `moderate` are named only where the caller asked** —
 *    "Anything to know", which gathers rows *because of* their severity and so
 *    cannot decline to say why. In their own sections `severe` speaks through
 *    the row's amber rule and `moderate` deliberately says nothing (it is
 *    neither calm enough to stamp clean nor severe enough to alarm).
 */
export function severityChip(
  fact: Fact,
  options: { readonly off: boolean; readonly severityStamp: boolean },
): SeverityChip | null {
  if (options.off) return null;
  if (fact.severity === 'none') {
    return fact.fact_type === 'records_check' ? { label: 'Clear', ink: 'clear-ink' } : null;
  }
  if (fact.severity === 'info') return { label: 'Note', ink: null };
  if (!options.severityStamp) return null;
  if (fact.severity === 'severe') return { label: 'Severe', ink: 'red-ink' };
  if (fact.severity === 'moderate') return { label: 'Moderate', ink: 'amber-ink' };
  return null;
}

/**
 * Whether the row takes the calm green rule.
 *
 * **Severity and nothing else**, deliberately wider than the `Clear` chip above:
 * the rule is a *treatment* — how alarmed the page is about this row — and the
 * chip is a *claim*. A witness-role litigation is genuinely calm and should read
 * calm, whatever the reason; it just may not be described as a clean search.
 * Only a type whose severity the catalog computes reaches `none` at all, so a
 * valence-less row (`normal` → `null` on the wire, `wireSeverityFor`) can never
 * take this rule by accident.
 *
 * **`none`-only, and `info` does not join it** (2026-09-02). The green rule is
 * the page saying "there is nothing here"; a `note` is the page saying "there is
 * something here, and it is not adverse". Those are different sentences, and
 * painting the second one green would file a self-disclosure among the record's
 * good news, which is the one thing the `note` tier exists to stop. `info` gets
 * the neutral `Note` stamp and no rule at all — the same nothing `moderate`
 * takes in its own section, which is the honest treatment for a row that is
 * neither news nor reassurance.
 */
export function readsClear(fact: Fact, options: { readonly off: boolean }): boolean {
  return !options.off && fact.severity === 'none';
}
